import { createElement as h } from "preact";
import { useState, useEffect, useMemo } from "preact/hooks";
import classnames from "classnames";

import { avg, vertexEvents, signEquals } from "./helper.js";
import Stone from "../Stone.js";

const absoluteStyle = (zIndex) => ({
  position: "absolute",
  zIndex,
});

export default function Vertex(props) {
  let {
    position,
    shift,
    random,
    sign,
    marker,
    current,
    ghostStone,
    isLoading, 
    animate,
    selected,
    selectedLeft,
    selectedRight,
    selectedTop,
    selectedBottom,
    moveScore, 
    height, 
    complexity, 
    weight, 
    showMarkers,
    showAnalysis, 
    evaluationThresholds,
    stoneMenu, handleStoneMenu, closeStoneMenu
  } = props;

  const [isHovered, setIsHovered] = useState(false); // Hover state
  const [dialogVisible, setDialogVisible] = useState(false); // Dialog visibility

  // Refactored code using useMemo() without useCallback()
  // AO 241202
  const eventHandlers = useMemo(() => {
    const handlers = {};
    for (let eventName of vertexEvents) {
      handlers[eventName] = (evt) => {
        props[`on${eventName}`]?.(evt, position);
      };
    }
    return handlers;
  }, [position, props]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (moveScore !== undefined) { // Only make the dialog appear if there's a score to show. 
      setDialogVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setDialogVisible(false);
  };

  // Close dialog when the vertex component unmounts
  useEffect(() => {
    if(moveScore === undefined) {
      setDialogVisible(false)
    }
    return () => {
      setDialogVisible(false); // Cleanup when the component disappears
    };
  }, []);
  
  const stoneDiameterBold = "95%"
  const stoneDiameter = "85%"
  const BW = ghostStone?.sign === 1 ? "0" : "255"
  const stoneBorderStyle = `.03em solid rgba(${BW},${BW},${BW},.8)`
  const stoneBorderStyleBold = `.09em solid rgba(${BW},${BW},${BW},.9)`

  return h(
    "div",
    Object.assign(
      {
        "data-x": position[0],
        "data-y": position[1],

        title: marker?.label,
        style: {
          position: "relative",
          width: "100%", 
          height: "100%", 
        },
        className: classnames(
          "shudan-vertex",
          `shudan-sign_${sign}`,
        ),
      },
      ...vertexEvents.map((eventName) => ({
        [`on${eventName}`]: eventHandlers[eventName],
      }))
    ),

    // This draws a suggested move as well as the next move
    !!ghostStone && h("div", { 
      style: {
            border: ghostStone?.nextMove ? stoneBorderStyleBold : stoneBorderStyle,
            boxShadow: "0.02em .05em .05em rgba(23, 10, 2, .3)", 
            boxSizing: "border-box",
            position: "absolute",
            transform: "translate(-50%, -50%)",
            left: "50%",
            top: "50%",
            backgroundColor: `${ghostStone?.color}`,
            width: ghostStone?.nextMove ? stoneDiameterBold : stoneDiameter,
            height: ghostStone?.nextMove ? stoneDiameterBold : stoneDiameter,
            borderRadius: "50%",
            cursor: "pointer",
      }, 
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }), 

    // Dialog box rendering
    dialogVisible &&
    h("div",
      {
        key: "dialog",
        style: {
          position: "absolute",
          top: position[1] > (height-1)/2 ? "-120%" : "110%", // Position below the vertex
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column", // Stack items vertically
          justifyContent: "center",
          alignItems: "center", // Align text lines to the left
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          border: "1px solid black",
          borderRadius: "5px",
          padding: "2px",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
          zIndex: 2000000,
          fontSize: "14px", // Adjust font size as needed
          lineHeight: "1.2em", 
          pointerEvents: "none", 
        },
      }, moveScore !== undefined ? `${Math.round(10 * moveScore)/10}` : setDialogVisible(false)
      // [
      //   moveScore !== undefined && h("div", {}, `Move Score: ${Number(moveScore.toFixed(1))}`),
      //   complexity !== undefined && h("div", {}, `Complexity: ${complexity}`),
      //   weight !== undefined && h("div", {}, `Weight: ${weight}`)
      // ]
    ), 

    // // We generate the marker either if this vertex is the next move 
    // // and we want to put a move number or there's a stone here 
    // // and we want to show its evaluation:
    // (current && !!sign) && markerMarkup(10),

    !!sign && h(
      Stone,
      Object.assign({
        sign: sign, 
        moveNumber: marker?.moveNumber, 
        // nodeId: "asldkfjsldkfj", 
        move: marker?.move, 
        alternativeMoves: marker?.alternativeMoves, 
        alternativeSimplestMoves: marker?.alternativeSimplestMoves,
        showMarkers: showMarkers,
        showAnalysis: showAnalysis, 
        moveData: marker?.moveData,
        evaluationThresholds: evaluationThresholds, 
        stoneMenu: stoneMenu, 
        handleStoneMenu: handleStoneMenu, 
        closeStoneMenu: closeStoneMenu,
      })
    ),

    // Conditionally render the loading spinner
    isLoading &&
    h("div", {
      className: "loading-spinner-wrapper",
      style: {
        width: "100%", // Adjust size
        height: "100%", // Adjust size
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        position: "absolute", 
        zIndex: 100,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    }, h("div", {
      className: "loading-spinner", 
      style: {
          top: ".04em", 
          left: ".04em",
          animation: "spin 1s linear infinite",
          border: "0.13em solid rgba(0, 0, 0, 0.1)", // Light border
          borderTop: "0.13em solid rgba(255, 255, 255, 0.7)", // Dark border
          borderRadius: "50%",
          boxSizing: "border-box", 
          width: "66%",
          height: "66%",
          zIndex: 100,
      }
    })), 

    !!selected &&
      h("div", {
        key: "selection",
        className: "shudan-selection",
        style: absoluteStyle(4),
      }),
  );
}
